import React, { useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../components/Pagination";
import DropdownFilter from "utils/DropdownFilter";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import SearchComponent from "./SearchComponent";
import { Avatar } from "@mui/material";
import constants from "constants";
import "assets/css/listing-page.css";
import {
  Card,
  Container,
  Row,
  Button,
  UncontrolledTooltip,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner
} from "reactstrap";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import helpers from "utils/helpers";
import { useAuth } from "context/AuthContext";
import { useTranslation } from "context/TranslationProvider";
import { useNavigate } from "react-router-dom";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import RejectListingModal from "modals/RejectListingModal";
import senderLogo from "assets/img/images/sender.svg";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import useNotificationHighlight from "hooks/useNotificationHighlight";

function ListingTable({
  items = [],
  isItemsLoading,
  handleToggleAccount,
  rotate,
  searchFrom,
  searchTo,
  isSelectedFrom,
  isSelectedTo,
  filteredCities = [],
  handleFromChange,
  handleToChange,
  handleFromCity,
  handleToCity,
  toggleFromTo,
  startDate,
  setStartDate,
  today,
  noCityMatchMessage,
  deleteRow,
  onTableChange,
  meta = { totalItems: 0, totalPages: 1, page: 1, limit: 20 },
  onSearchClick,
  currentStatus,
  currentListingType,
  sortBy,
  sortOrder,
  isStatusUpdating,
  statusLoadingId,
  onClearFilters,
  handleOpenUserDetails
}) {
  const { t, language } = useTranslation();
  const { isHighlighted } = useNotificationHighlight();
  const navigate = useNavigate();
  const { permission } = useAuth();
  const listingPermission = permission?.permissions?.modules?.listings;
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedRowForReject, setSelectedRowForReject] = useState(null);


  const handleFilterSelect = (val) => {
    if (val === "all") {
      onTableChange("filter", {
        filters: { status: "", listingType: "" }
      });
    } else if (val === "Sender" || val === "Traveler") {
      onTableChange("filter", {
        filters: { status: "", listingType: val }
      });
    } else {
      onTableChange("filter", {
        filters: { status: val, listingType: "" }
      });
    }
  };

  return (
    <>
      <SimpleHeader name={t("sideNav.adminListings", "Listings")} parentName={t("sideNav.dashboard", "Dashboard")} />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
          <ToolkitProvider
            data={items}
            keyField="_id"
            key={language}
            columns={[
              {
                dataField: "name",
                text: t("table.headers.name", "Name"),
                headerAlign: "left",
                align: "left",
                formatter: (_, row) => {
                  const profileFile = row?.userData?.profilePictureUrl;
                  const picUrl = profileFile
                    ? `${constants.s3Bucket}/profile-pics/${profileFile}`
                    : null;

                  const firstName = row?.userData?.firstName || "";
                  const lastName = row?.userData?.lastName || "";
                  const isVerified = row?.userData?.isVerified === true;

                  const verifyId = `verify-${row._id}`;
                  const travelerId = `traveler-${row._id}`;
                  const senderId = `sender-${row._id}`;

                  return (
                    <div className="d-flex align-items-center">
                      {picUrl ? (
                        <Avatar src={picUrl} sx={{ width: 32, height: 32 }} />
                      ) : (
                        <Avatar
                          {...helpers.stringAvatar(`${firstName} ${lastName}`)}
                          sx={{ width: 32, height: 32, fontSize: 14 }}
                        />
                      )}

                      <div className="ml-2 d-flex align-items-center gap-1">
                        <span
                          id={`user-name-${row._id}`}
                          className="text-primary cursor-pointer font-weight-bold"
                          onClick={() => {
                            if (row?.type && row?._id) {
                              navigate(`/admin/listing-details/${row.type.toLowerCase()}/${row._id}`);
                            }
                          }}
                        >
                          {firstName} {lastName}
                        </span>

                        <UncontrolledTooltip target={`user-name-${row._id}`} key={`tooltip-user-${row._id}-${language}`}>
                          {t("tooltip.viewListingDetails", "View listing details")}
                        </UncontrolledTooltip>

                        {isVerified ? (
                          <>
                            <VerifiedIcon
                              id={verifyId}
                              className="icon-status verifiedIcon"
                              tabIndex={0}
                              role="img"
                              aria-label={t("page.user-profile.tooltip.verifiedUser", "Verified User")}
                            />
                            <UncontrolledTooltip target={verifyId}>
                              {t("page.user-profile.tooltip.verifiedUser", "Verified User")}
                            </UncontrolledTooltip>
                          </>
                        ) : (
                          <>
                            <CancelIcon
                              id={verifyId}
                              className="icon-status unverifiedIcon"
                              tabIndex={0}
                              role="img"
                              aria-label={t("page.user-profile.tooltip.unverifiedUser", "Unverified User")}
                            />
                            <UncontrolledTooltip target={verifyId}>
                              {t("page.user-profile.tooltip.unverifiedUser", "Unverified User")}
                            </UncontrolledTooltip>
                          </>
                        )}

                        {row?.type === "Traveler" && (
                          <>
                            <div
                              id={travelerId}
                              className="listings-icon-box ml-2"
                              tabIndex={0}
                              role="img"
                              aria-label={t("page.users.travelerListings", "Traveler Listing")}
                            >
                              <PersonOutlinedIcon
                                className="listings-icon-traveler"
                                fontSize="small"
                              />
                            </div>
                            <UncontrolledTooltip target={travelerId} placement="top">
                              {t("page.users.travelerListings", "Traveler Listing")}
                            </UncontrolledTooltip>
                          </>
                        )}

                        {row?.type === "Sender" && (
                          <>
                            <div
                              id={senderId}
                              className="listings-icon-box ml-2"
                              tabIndex={0}
                              role="img"
                              aria-label={t("page.users.senderListings", "Sender Listing")}
                            >
                              <img
                                src={senderLogo}
                                alt={t("page.users.senderListings", "Sender Listing")}
                                className="listings-icon-sender-img"
                              />
                            </div>
                            <UncontrolledTooltip target={senderId} placement="top">
                              {t("page.users.senderListings", "Sender Listing")}
                            </UncontrolledTooltip>
                          </>
                        )}
                      </div>
                    </div>
                  );
                }
              },

              {
                dataField: "fromPlace.name",
                text: t("table.headers.from", "From"),
                headerAlign: "left",
                align: "left",
                sort: true,
              },
              {
                dataField: "toPlace.name",
                text: t("table.headers.to", "To"),
                headerAlign: "left",
                align: "left",
                sort: true,
              },

              {
                dataField: "price",
                text: t("table.headers.price", "Price"),
                headerAlign: "left",
                align: "left",
                formatter: (_, row) => {
                  const details =
                    row?.baggageDetails ||
                    row?.packageDetails ||
                    row?.listing?.baggageDetails ||
                    row?.listing?.packageDetails;

                  if (!details?.price) return "-";

                  const symbol =
                    details?.currency?.symbol && details.currency.symbol.trim() !== ""
                      ? details.currency.symbol
                      : helpers.DEFAULT_CURRENCY;

                  return `${symbol}${details.price}`;
                },
              },

              {
                dataField: "date",
                text: t("page.orders.journeyDate", "Journey Date"),
                sort: true,
                headerAlign: "left",
                align: "left",
                formatter: (cell, row) =>
                  row?.date ? helpers.getFormattedDate(row.date, t) : "",
              },

              {
                dataField: "status",
                text: t("table.headers.status", "Status"),
                align: "left",
                headerAlign: "left",
                formatter: (cell, row, rowIndex, formatExtraData) => {
                  const statusLoadingId = formatExtraData;
                  const currentStatus = (cell || "pending").toLowerCase();
                  const isDraft = row?.isDraft === true || currentStatus === "draft";
                  const isExpired = helpers.isExpired(row) || currentStatus === "expired";
                  const statusClass = helpers.getStatusClass(currentStatus, "listing");

                  const canUpdate = listingPermission?.update;
                  const disabled = !canUpdate || isDraft || isExpired || row?.isDeleted || statusLoadingId === row._id;

                  if (isDraft) {
                    return (
                      <span className="draft-listing-pill">
                        {t("page.userListings.status.draft", "Draft")}
                      </span>
                    );
                  }

                  if (isExpired && currentStatus !== "closed" && currentStatus !== "rejected") {
                    return (
                      <span className="expired-listing-pill">
                        {t("page.userListings.status.expired", "Expired")}
                      </span>
                    );
                  }

                  return (
                  <>
                    <UncontrolledDropdown>
                      <DropdownToggle
                        caret
                        size="sm"
                        className={`status-dropdown ${statusClass} ${disabled ? "cursor-not-allowed" : ""}`}
                        id={`status-dropdown-${row._id}`}
                        disabled={disabled}
                      >
                        {t(`page.userListings.status.${currentStatus}`)}
                      </DropdownToggle>
                      <DropdownMenu>
                        {Object.keys(helpers.getStatusArray("listing"))
                          .filter((s) => {
                            if (s === "draft") return false;
                            const isTraveler = row?.type?.toLowerCase() === "traveler";
                            const isPending = s === "pending";
                            if (isTraveler && isPending) return false;
                            return true;
                          })
                          .map((s) => (
                            <DropdownItem
                              key={s}
                              disabled={s === currentStatus}
                              onClick={() => {
                                if (s === "rejected") {
                                  setSelectedRowForReject(row);
                                  setIsRejectModalOpen(true);
                                } else {
                                  handleToggleAccount(row, s, row.type);
                                }
                              }}
                            >
                              {t(`page.userListings.status.${s}`)}
                            </DropdownItem>
                          ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                    {(row?.isDeleted || !canUpdate) && statusLoadingId !== row._id && (
                      <UncontrolledTooltip
                        target={`status-dropdown-${row._id}`}
                      >
                        {row?.isDeleted ? t("tooltip.alreadyDeleted", "Listing already deleted") : t("tooltip.noPermission", "Permission denied")}
                      </UncontrolledTooltip>
                    )}
                  </>
                  );
                },
                formatExtraData: statusLoadingId,
              },

              {
                dataField: "createdAt",
                text: t("table.headers.createdAt", "Created At"),
                sort: true,
                headerAlign: "left",
                align: "left",
                formatter: (cell, row) =>
                  row?.createdAt ? helpers.getFormattedDate(row.createdAt, t) : "",
              },
              {
                text: t("table.headers.action", "Action"),
                dataField: "action",
                headerAlign: "left",
                align: "left",
                formatter: (_, row, rowIndex, formatExtraData) => {
                  const statusLoadingId = formatExtraData;
                  if (!listingPermission?.read && !listingPermission?.delete) {
                    return <div className="text-center text-muted w-100">N/A</div>;
                  }

                const isDraft = (row?.status || "pending") === "draft" && !row?.isDeleted;
                const isViewDisabled = isDraft || row?.isDeleted;

                return (
                  <div className="d-flex align-items-center gap-2">
                    {/* View Button */}
                    <Button
                      id={`view-${row._id}`} 
                      size="sm"
                      key={`view-${row._id}-${language}`}
                      disabled={isViewDisabled}
                      onClick={() =>
                        navigate(`/admin/listing-details/${row.type.toLowerCase()}/${row._id}`)
                      }
                    >
                      <VisibilityIcon fontSize="small" color="primary" />
                    </Button>
                    <UncontrolledTooltip 
                      target={`view-${row._id}`}
                      key={`view-tooltip-${row._id}-${language}`}
                    >
                      {isDraft
                        ? t("tooltip.draftNotViewable", "Draft listing cannot be viewed")
                        : row?.isDeleted
                        ? t("tooltip.alreadyDeleted", "Listing already deleted")
                        : t("tooltip.viewListing", "View listing details")}
                    </UncontrolledTooltip>

                      <Button
                        id={`delete-${row._id}`}
                        size="sm"
                        disabled = {
                          !listingPermission?.delete ||
                          row?.status === "resolved" ||
                          row?.isDeleted === true ||
                          statusLoadingId === row._id
                        }
                        onClick={() => deleteRow(row)}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </Button>

                      <UncontrolledTooltip target={`delete-${row._id}`}>
                        {!listingPermission?.delete
                          ? t("tooltip.noPermission", "Permission denied")
                          : row?.isDeleted
                          ? t("tooltip.alreadyDeleted", "Listing already deleted")
                          : t("tooltip.deleteListing", "Delete listing")}
                      </UncontrolledTooltip>
                    </div>
                  );
                },
                formatExtraData: statusLoadingId,
              }
            ]}
          >
            {(props) => (
              <div className="py-4">
                <div className="listing-header-row px-4 pb-1 mb-3">
                  <div className="listing-header-left">
                    <span className="text-sm font-medium text-slate-600">{t("table.search", "Search")}:</span>

                    <SearchComponent
                      rotate={rotate}
                      searchFrom={searchFrom}
                      searchTo={searchTo}
                      isSelectedFrom={isSelectedFrom}
                      isSelectedTo={isSelectedTo}
                      filteredCities={filteredCities}
                      handleFromChange={handleFromChange}
                      handleToChange={handleToChange}
                      handleFromCity={handleFromCity}
                      handleToCity={handleToCity}
                      toggleFromTo={toggleFromTo}
                      startDate={startDate}
                      setStartDate={setStartDate}
                      today={today}
                      onSearchClick={onSearchClick}
                      noCityMatchMessage={noCityMatchMessage}
                    />
                  </div>

                  <div className="listing-header-right d-flex align-items-center">
                    <span className="text-sm font-weight-600 text-muted mr-2">
                      {t("table.filterBy", "Filter By")}
                    </span>

                    <DropdownFilter
                      value={currentListingType || currentStatus || "all"}
                      options={helpers.getFilter("LISTING_FILTER", t)}
                      onChange={handleFilterSelect}
                      buttonClass="btn-neutral filter-select2"
                    />

                    {onClearFilters && (
                      <Button
                        size="sm"
                        className="ml-2 btn-neutral text-danger font-weight-600"
                        onClick={onClearFilters}
                      >
                        {t("common.clearFilters", "Clear Filters")}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="table-responsive">
                  <BootstrapTable
                    {...props.baseProps}
                    data={items}
                    remote={{ sort: true }}
                    onTableChange={onTableChange}
                    rowClasses={(row) => (isHighlighted("listing", row?._id) ? "bg-[#F0F7FF] border-left-highlight" : "")}
                    sort={{
                      dataField: sortBy || "date",
                      order: sortOrder === 1 ? "asc" : "desc"
                    }}
                    bootstrap4
                    bordered={false}
                    noDataIndication={() => (
                      <div className="text-center py-6 text-muted d-flex flex-column align-items-center">
                        {isItemsLoading ? (
                          <div className="center-loader-content">
                            <Spinner />
                            <p className="mt-3 text-muted">{t("common.loading", "Loading....")}</p>
                          </div>
                        ) : (
                          <span className="text-sm md:text-base">
                            {t("analytics.dashboard.notFound", "No listings found")}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
                <Pagination
                  totalRows={meta.totalItems}
                  rowsPerPage={meta.limit}
                  currentPage={meta.page - 1}
                  onPageChange={(page) => onTableChange("pagination", { page: page + 1 })}
                  onRowsPerPageChange={(size) => onTableChange("pagination", { page: 1, sizePerPage: size })}
                  t={t}
                />

              </div>
            )}
          </ToolkitProvider>
            </Card>
          </div>
        </Row>
      </Container>
      <RejectListingModal
        isOpen={isRejectModalOpen}
        toggle={() => {
          setIsRejectModalOpen(false);
          setSelectedRowForReject(null);
        }}
        onConfirm={async (adminRemarks) => {
          if (selectedRowForReject) {
            await handleToggleAccount(
              selectedRowForReject,
              "rejected",
              selectedRowForReject.type,
              null,
              adminRemarks
            );
            setIsRejectModalOpen(false);
            setSelectedRowForReject(null);
          }
        }}
        isLoading={isStatusUpdating}
      />
    </>
  );
}

export default ListingTable;
